import { css, html, nothing } from "lit";
import { artImage, type ChromeArt } from "../chrome/chromeArt";
import { fallbackLabels, type LabelTable, type Labels } from "../chrome/chromeLabels";
import { ChromeElement, defineChromeElement, pressFeedback } from "../chrome/ChromeElement";
import { fitTexts } from "../chrome/fitText";
import type { GameHistoryBindings, GameHistoryPort } from "./GameHistoryPort";
import type { FormattedHistoryEntry, GameHistoryTab } from "./GameHistoryTypes";
import { fitScale } from "@slot/presentation";

/** The static texts, localized by the factory. */
export const GAME_HISTORY_LABELS = {
  title: ["history.title", "Game History"],
  tabBetHistory: ["history.tab.bet_history", "Bet History"],
  tabBiggestWinsCurrent: ["history.tab.biggest_wins_current", "Big Wins Current Game"],
  columnDate: ["history.column.date", "Date"],
  columnBet: ["history.column.bet", "Bet"],
  columnWin: ["history.column.win", "Win"],
  loadMore: ["history.load_more", "Load More"],
  empty: ["history.empty", "No Data Available"],
  loading: ["common.loading", "Loading..."],
  clickToReplay: ["history.click_to_replay", "Click to Replay"],
  failed: ["history.failed", "Failed"],
} as const satisfies LabelTable;
export type GameHistoryLabels = Labels<typeof GAME_HISTORY_LABELS>;

/** `GameHistoryPanel.prefab` geometry (design units, y down). */
const BACKDROP_ALPHA = 232 / 255;
/** Header 1080×300, top-pinned, scale-mode: desktop max 1080×350, mobile max 1500 wide (height unlimited), landscape reference height 350. */
const HEADER = { width: 1080, height: 300, desktopMaxWidth: 1080, desktopMaxHeight: 350, mobileMaxWidth: 1500, landscapeReferenceHeight: 350, titleTop: 85.22, titleSize: 50, closeX: 404.1, closeTop: 82.9, closeSize: 110 } as const;
const TAB = { width: 500, height: 64, x: 300, top: 202.24, labelSize: 30.8 } as const;
/** Table 1500 wide pinned 350 below the top and stretched to the bottom; scale-mode max 1500 wide (landscape unlimited, reference height 1000). */
const TABLE = { width: 1500, height: 1570, top: 350, maxWidth: 1500, landscapeReferenceHeight: 1000 } as const;
/** The column header row (100 tall) and the four columns (left edges in the 1500-wide table). */
const COLUMNS = { date: { left: 0, width: 400 }, bet: { left: 400, width: 250 }, win: { left: 650, width: 250 }, replay: { left: 900, width: 400 } } as const;
const COLUMN_HEADER = { height: 100, labelSize: 46.6 } as const;
const LIST = { top: 110, rowHeight: 150, rowSpacing: 5, footerHeight: 200, textSize: 35.4, evenRowAlpha: 25 / 255 } as const;
const LOAD_MORE = { width: 235.7, height: 40, labelSize: 20 } as const;
const STATE_LABEL = { top: 185.5, size: 55.1, width: 640.5 } as const;

export const GAME_HISTORY_TAG = "slot-game-history";

/**
 * `<slot-game-history>`: backdrop (taps swallowed, no close), a header with the title, the close
 * button and the two tabs (the active tab renders white and takes no taps), the column header row,
 * and the scrolling row list with its load-more footer, empty and loading states. A row's replay
 * column reports the row's link (`onReplay`); the composition root opens it (decision 34). A failed
 * round's row (`isFailed`) is dimmed and shows its round id and "Failed" where the link would be. The
 * presenter builds it no link (review 2026-09-27 #7).
 */
export class GameHistoryElement extends ChromeElement implements GameHistoryPort {
  public static override properties = {
    ...ChromeElement.properties,
    open: { type: Boolean, reflect: true },
    labels: { attribute: false },
    art: { attribute: false },
    activeTab: { state: true },
    loading: { state: true },
    empty: { state: true },
    rows: { state: true },
    loadMoreVisible: { state: true },
    loadMoreLoading: { state: true },
  };

  public declare open: boolean;
  public declare labels: GameHistoryLabels;
  public declare art: ChromeArt;
  private declare activeTab: GameHistoryTab;
  private declare loading: boolean;
  private declare empty: boolean;
  private declare rows: FormattedHistoryEntry[];
  private declare loadMoreVisible: boolean;
  private declare loadMoreLoading: boolean;
  private bindings: GameHistoryBindings | null = null;

  public constructor() {
    super();
    this.open = false;
    this.labels = fallbackLabels(GAME_HISTORY_LABELS);
    this.art = {};
    this.activeTab = "bet_history";
    this.loading = false;
    this.empty = false;
    this.rows = [];
    this.loadMoreVisible = false;
    this.loadMoreLoading = false;
  }

  public static override styles = css`
    :host { display: none; position: absolute; inset: 0; pointer-events: auto; color: #fff; font-weight: 500; }
    :host([open]) { display: block; }
    .backdrop { position: absolute; inset: 0; background: rgba(0, 0, 0, ${BACKDROP_ALPHA}); }
    .header { position: absolute; left: 50%; top: 0; width: ${HEADER.width}px; height: ${HEADER.height}px; transform: translateX(-50%) scale(var(--header-scale, 1)); transform-origin: top center; }
    .title { position: absolute; left: 0; right: 0; top: ${HEADER.titleTop}px; transform: translateY(-50%); text-align: center; font-size: ${HEADER.titleSize}px; line-height: 1; white-space: nowrap; }
    .close { position: absolute; left: calc(50% + ${HEADER.closeX}px); top: ${HEADER.closeTop}px; width: ${HEADER.closeSize}px; height: ${HEADER.closeSize}px; transform: translate(-50%, -50%); margin: 0; padding: 0; border: 0; background: transparent no-repeat center / 100% 100%; cursor: pointer; touch-action: manipulation; -webkit-tap-highlight-color: transparent; }
    .close:hover { filter: brightness(0.39); }
    .close:active { filter: brightness(0.73); }
    .tab { position: absolute; top: ${TAB.top}px; width: ${TAB.width}px; height: ${TAB.height}px; transform: translate(-50%, -50%); border: 0; border-radius: 8px; padding: 0; background: #616161; color: #000; font: inherit; font-size: ${TAB.labelSize}px; font-weight: 500; cursor: pointer; touch-action: manipulation; -webkit-tap-highlight-color: transparent; }
    .tab:hover { background: #d3d3d3; }
    .tab:active, .tab[disabled] { background: #fff; cursor: default; }
    .tab.bet { left: calc(50% - ${TAB.x}px); }
    .tab.wins { left: calc(50% + ${TAB.x}px); }
    .table { position: absolute; left: 50%; top: ${TABLE.top}px; width: ${TABLE.width}px; transform: translateX(-50%) scale(var(--table-scale, 1)); transform-origin: top center; }
    .columns { position: relative; height: ${COLUMN_HEADER.height}px; border-top: 2px solid #fff; border-bottom: 2px solid #fff; box-sizing: border-box; display: flex; }
    .columns > div { display: flex; align-items: center; justify-content: center; font-size: ${COLUMN_HEADER.labelSize}px; border-right: 2px solid #fff; box-sizing: border-box; }
    .columns > div:last-child { border-right: 0; }
    .list { position: absolute; left: 0; right: 0; top: ${LIST.top}px; overflow-x: hidden; overflow-y: auto; overscroll-behavior: contain; touch-action: pan-y; scrollbar-color: #fff rgba(255, 255, 255, 0.075); }
    /* The prefab's 25-unit scroll bar: a faint track, a white handle. */
    .list::-webkit-scrollbar { width: 25px; }
    .list::-webkit-scrollbar-track { background: rgba(255, 255, 255, 0.075); }
    .list::-webkit-scrollbar-thumb { background: #fff; border-radius: 12.5px; }
    .row { display: flex; height: ${LIST.rowHeight}px; margin-bottom: ${LIST.rowSpacing}px; font-size: ${LIST.textSize}px; line-height: 1.15; }
    .row:nth-child(odd) { background: rgba(255, 255, 255, ${LIST.evenRowAlpha}); }
    .row > div { border-right: 2px solid #fff; box-sizing: border-box; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; overflow: hidden; }
    .row > div:last-child { border-right: 0; }
    /* A failed round (review 2026-09-27 #7): its text dims and the column rules stay white, so the table stays one grid. */
    .row.failed { color: rgba(255, 255, 255, 0.5); }
    .col-date { position: relative; text-align: left !important; }
    .col-date > div { position: absolute; left: 20px; width: 360px; display: flex; align-items: center; transform: translateY(-50%); }
    .name { top: 47.5px; height: 80.78px; white-space: pre; }
    .date { top: 119.05px; height: 50.4px; font-weight: 300; white-space: nowrap; }
    .col-replay { position: relative; }
    .col-replay > div { position: absolute; left: 0; right: 0; transform: translateY(-50%); }
    .col-replay > div:first-child { top: 49.16px; }
    .col-replay > div:last-child { top: 102.18px; }
    [data-fit] { display: inline-block; max-width: none; transform-origin: left center; }
    .row > div:not(.col-date) [data-fit] { transform-origin: center; white-space: nowrap; }
    .replay { cursor: pointer; text-decoration: underline; text-underline-offset: 4px; }
    .footer { height: ${LIST.footerHeight}px; display: flex; align-items: center; justify-content: center; }
    .load-more { width: ${LOAD_MORE.width}px; height: ${LOAD_MORE.height}px; border: 0; border-radius: 8px; padding: 0; background: #fff; color: #000; font: inherit; font-size: ${LOAD_MORE.labelSize}px; font-weight: 500; cursor: pointer; }
    .load-more:hover { filter: brightness(0.83); }
    .load-more[disabled] { filter: brightness(0.49); cursor: default; }
    .state { position: absolute; left: 50%; top: ${STATE_LABEL.top}px; transform: translate(-50%, -50%); width: ${STATE_LABEL.width}px; text-align: center; font-size: ${STATE_LABEL.size}px; line-height: 1.15; }
  `;

  public bind(bindings: GameHistoryBindings): void {
    this.bindings = bindings;
  }

  public setOpen(open: boolean): void {
    this.open = open;
  }

  public setActiveTab(tab: GameHistoryTab): void {
    this.activeTab = tab;
  }

  public setLoading(loading: boolean): void {
    this.loading = loading;
    if (loading) this.empty = false;
  }

  public setEntries(entries: FormattedHistoryEntry[]): void {
    this.rows = [...entries];
    this.empty = entries.length === 0;
  }

  public appendEntries(entries: FormattedHistoryEntry[]): void {
    this.rows = [...this.rows, ...entries];
  }

  public setLoadMoreVisible(visible: boolean): void {
    this.loadMoreVisible = visible;
  }

  public setLoadMoreLoading(loading: boolean): void {
    this.loadMoreLoading = loading;
  }

  public setEmptyState(visible: boolean): void {
    this.empty = visible;
  }

  protected override updated(): void {
    fitTexts(this.renderRoot);
  }

  public resetScrollToTop(): void {
    const list = this.renderRoot.querySelector<HTMLElement>(".list");
    if (list) list.scrollTop = 0;
  }

  /** The two authored window layouts plus the table's widget stretch (top 350 → bottom), per viewport. */
  private layout() {
    const v = this.view.design;
    const h = HEADER;
    const headerScale = fitScale(
      { width: h.width, height: this.isLandscape ? h.landscapeReferenceHeight : h.height },
      this.isDesktop ? { maxWidth: h.desktopMaxWidth, maxHeight: h.desktopMaxHeight } : { maxWidth: h.mobileMaxWidth },
      v,
    );
    const tableScale = fitScale({ width: TABLE.width, height: this.isLandscape ? TABLE.landscapeReferenceHeight : TABLE.height }, this.isLandscape ? {} : { maxWidth: TABLE.maxWidth }, v);
    const tableInnerHeight = Math.max(1, v.height - TABLE.top) / tableScale;
    // The prefab centred the 300-tall header box on its (landscape: 350) reference height: the box sits lower by half the difference.
    const headerTop = (((this.isLandscape ? h.landscapeReferenceHeight : h.height) - h.height) * headerScale) / 2;
    return { headerScale, headerTop, tableScale, tableInnerHeight };
  }

  protected override render() {
    const { headerScale, headerTop, tableScale, tableInnerHeight } = this.layout();
    const l = this.labels;
    const showTable = !this.loading && !this.empty;
    return html`
      <div class="backdrop"></div>
      <div class="header" style="--header-scale: ${headerScale}; top: ${headerTop}px">
        <div class="title" data-part="title">${l.title}</div>
        <button class="close" data-part="close" type="button" style=${artImage(this.art, "closeRed")} @pointerdown=${pressFeedback} @click=${() => this.bindings?.onClose()}></button>
        <button class="tab bet" data-part="tab-bet-history" type="button" ?disabled=${this.activeTab === "bet_history"} @pointerdown=${pressFeedback} @click=${() => this.bindings?.onTabBetHistory()}>${l.tabBetHistory}</button>
        <button class="tab wins" data-part="tab-biggest-wins" type="button" ?disabled=${this.activeTab === "biggest_wins_current"} @pointerdown=${pressFeedback} @click=${() => this.bindings?.onTabBiggestWinsCurrent()}>${l.tabBiggestWinsCurrent}</button>
      </div>
      <div class="table" style="--table-scale: ${tableScale}; height: ${tableInnerHeight}px">
        ${showTable
          ? html`<div class="columns">
              <div style="width: ${COLUMNS.date.width}px">${l.columnDate}</div>
              <div style="width: ${COLUMNS.bet.width}px">${l.columnBet}</div>
              <div style="width: ${COLUMNS.win.width}px">${l.columnWin}</div>
              <div style="width: ${COLUMNS.replay.width}px"></div>
            </div>`
          : nothing}
        <div class="list" style="height: ${Math.max(1, tableInnerHeight - LIST.top)}px" ?hidden=${!showTable}>
          ${this.rows.map(
            (row) => html`<div class=${row.isFailed ? "row failed" : "row"} data-part="row">
              <div class="col-date" style="width: ${COLUMNS.date.width}px"><div class="name"><span data-fit data-part="game-name">${row.gameName}</span></div><div class="date"><span data-fit data-part="date">${row.dateText}</span></div></div>
              <div style="width: ${COLUMNS.bet.width}px"><span data-fit data-part="bet">${row.betText}</span></div>
              <div style="width: ${COLUMNS.win.width}px"><span data-fit data-part="win">${row.winText}</span></div>
              <div style="width: ${COLUMNS.replay.width}px" class=${row.replayUrl ? "col-replay replay" : "col-replay"} data-part="replay" @click=${() => { if (row.replayUrl) this.bindings?.onReplay(row.replayUrl); }}>
                ${row.isFailed
                  ? html`<div data-part="round-id">${row.roundId}</div><div data-part="failed">${l.failed}</div>`
                  : row.replayUrl
                    ? html`<div data-part="round-id">${row.roundId}</div><div>${l.clickToReplay}</div>`
                    : nothing}
              </div>
            </div>`,
          )}
          ${this.loadMoreVisible
            ? html`<div class="footer"><button class="load-more" data-part="load-more" type="button" ?disabled=${this.loadMoreLoading} @pointerdown=${pressFeedback} @click=${() => this.bindings?.onLoadMore()}>${l.loadMore}</button></div>`
            : nothing}
        </div>
        ${this.loading ? html`<div class="state" data-part="loading">${l.loading}</div>` : nothing}
        ${this.empty && !this.loading ? html`<div class="state" data-part="empty">${l.empty}</div>` : nothing}
      </div>
    `;
  }
}

export function defineGameHistoryElement(): void {
  defineChromeElement(GAME_HISTORY_TAG, GameHistoryElement);
}
